import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionCostEntry, 
  CostFilters, 
  CostSummary 
} from '../types/construction-cost.types';

export const ConstructionCostService = {
  /**
   * Danh sách chi phí thi công
   */
  async getCosts(companyId: string, filters: CostFilters = {}): Promise<{ data: ConstructionCostEntry[], total: number }> {
    let query = supabase
      .from('construction_cost_entries')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        work_order:work_orders(id, title, order_code),
        recorder:user_profiles!recorded_by(id, full_name, avatar_url:avatar_path)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('cost_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.zoneId) query = query.eq('zone_id', filters.zoneId);
    if (filters.search) {
      query = query.or(`description.ilike.%${filters.search}%,source_id.ilike.%${filters.search}%`);
    }

    query = query.order('cost_date', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionCostEntry[],
      total: count || 0
    };
  },

  /**
   * Chi tiết mục chi phí
   */
  async getCostDetail(id: string): Promise<ConstructionCostEntry> {
    const { data, error } = await supabase
      .from('construction_cost_entries')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, name),
        work_order:work_orders(id, title, order_code),
        recorder:user_profiles!recorded_by(id, full_name, avatar_url:avatar_path)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionCostEntry;
  },

  /**
   * Tổng hợp chi phí dự án
   */
  async getSummary(companyId: string, projectId?: string): Promise<CostSummary> {
    let query = supabase
      .from('construction_cost_entries')
      .select('cost_type, actual_amount, planned_amount')
      .eq('company_id', companyId);

    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const summary: CostSummary = {
      totalBudget: 0,
      totalActual: 0,
      variance: 0,
      laborCost: 0,
      materialCost: 0,
      equipmentCost: 0,
      reworkCost: 0,
      variationCost: 0,
    };

    data.forEach(entry => {
      summary.totalBudget += (entry.planned_amount || 0);
      summary.totalActual += (entry.actual_amount || 0);
      
      switch (entry.cost_type) {
        case 'labor': summary.laborCost += entry.actual_amount; break;
        case 'material': summary.materialCost += entry.actual_amount; break;
        case 'equipment': summary.equipmentCost += entry.actual_amount; break;
        case 'rework': summary.reworkCost += entry.actual_amount; break;
        case 'variation': summary.variationCost += entry.actual_amount; break;
      }
    });

    summary.variance = summary.totalBudget - summary.totalActual;
    return summary;
  },

  /**
   * Cập nhật trạng thái phê duyệt (QS/PM Review)
   */
  async updateStatus(id: string, status: string, note?: string): Promise<void> {
    const { error } = await supabase
      .from('construction_cost_entries')
      .update({ status_code: status, note: note })
      .eq('id', id);

    if (error) throw error;
  }
};
